/** V4 skill progression: per-tree XP pools, fork-based choices, auto-unlock for non-fork nodes. */
import Phaser from 'phaser';
import { Component } from './Component';
import { ConfigLoader } from '../systems/ConfigLoader';
import { DebugLog } from '../debug/DebugLog';
import type { V4TreeProgress, V4NodeDefinition, V4ApotheosisDefinition, RecordUseOptions } from '../utils/Types';
import { exportTreeProgress, importTreeProgress } from './SkillProgressionPersistence';
import { computeEffectiveness, computeSkillSuccessChance, rollSkillSuccess as rollSkillSuccessFn } from './SkillMasteryCalculator';

/** Emitted when XP crosses a fork threshold and the player must choose. */
export interface ForkEvent {
  treeName: string;
  forkId: string;
  options: string[];
}

/** Emitted when a non-fork node auto-unlocks. */
export interface NodeUnlockEvent {
  treeName: string;
  nodeId: string;
  nodeName: string;
  nodeType: string;
}

/** V4 skill progression — single-path trees with fork choices and auto-unlock. */
export class SkillProgressionComponent implements Component {
  private treeProgress: Map<string, V4TreeProgress> = new Map();
  private events: Phaser.Events.EventEmitter;
  /** Track which nodes have already fired unlock events (prevents re-firing on load). */
  private firedUnlocks: Set<string> = new Set();
  /** When true, suppress unlock/fork events (used during respec and import). */
  private suppressEvents = false;

  constructor(events: Phaser.Events.EventEmitter) {
    this.events = events;
    this.initializeTrees();
  }

  private initializeTrees(): void {
    for (const treeName of Object.keys(ConfigLoader.v4Trees)) {
      this.treeProgress.set(treeName, { xp: 0, uses: 0, forkChoices: {} });
      // Seed xpThreshold-0 nodes into firedUnlocks so they don't emit
      // spurious node-unlocked events on first XP gain.
      const tree = ConfigLoader.v4Trees[treeName];
      for (const node of ConfigLoader.getV4AllNodes(treeName)) {
        if (node.xpThreshold === 0) this.firedUnlocks.add(node.id);
      }
    }
  }

  /** Award XP to the skill's tree for a successful use, applying multipliers. */
  recordUse(skillId: string, options?: RecordUseOptions): void {
    const treeName = ConfigLoader.getV4SkillTree(skillId);
    if (!treeName) return;
    const tree = this.treeProgress.get(treeName);
    if (!tree) return;

    // Only allow XP for skills the player actually has unlocked
    if (!this.isUnlocked(skillId)) return;

    tree.uses++;

    const xpConfig = ConfigLoader.xpGainConfig;
    let xp = xpConfig.baseXpPerUse;
    const failed = options?.failed ?? false;
    if (failed) {
      xp *= xpConfig.failedUseMultiplier;
    } else if (options?.toughEnemy) {
      xp *= xpConfig.toughEnemyMultiplier;
    }

    // Threshold-based XP scaling: higher-tier skills award more, relevance decays as tree outgrows the skill
    const node = ConfigLoader.getV4Node(skillId);
    const threshold = node?.xpThreshold ?? 0;
    const { thresholdScale, relevanceFloor } = xpConfig;
    const tierScale = 1 + threshold / thresholdScale;
    const relevance = Math.min(1, (threshold + relevanceFloor) / (tree.xp + relevanceFloor));
    xp *= tierScale * relevance;

    // Danger multiplier
    const danger = options?.danger ?? 1;
    xp *= xpConfig.dangerXpFloor + (1 - xpConfig.dangerXpFloor) * danger;

    // Effectiveness multiplier — only on successful uses (signals are meaningless on a fizzle)
    let eff = 1.0;
    if (!failed) {
      const effConfig = ConfigLoader.effectivenessConfig;
      if (effConfig.enabled) {
        eff = computeEffectiveness(node?.effectiveness, options, effConfig.defaultCap);
        xp *= eff;
      }
    }

    // Step fraction
    if (options?.stepFraction !== undefined) {
      xp *= options.stepFraction;
    }

    tree.xp += xp;

    DebugLog.log('combat', `XP +${xp.toFixed(2)} → ${treeName} (total: ${tree.xp.toFixed(1)}, skill: ${skillId}, eff: ${eff.toFixed(2)})`, {
      xp, treeName, skillId, totalXp: tree.xp, danger: options?.danger, eff,
    });

    this.events.emit('tree-xp-gained', { treeName, skillId, xpGained: xp, totalXp: tree.xp, uses: tree.uses });

    // Check for new unlocks after XP gain
    this.checkUnlocks(treeName);
  }

  /** Grant raw XP to a tree (debug use). Fires unlock events including evolutions. */
  grantXp(treeName: string, amount: number): void {
    const tree = this.treeProgress.get(treeName);
    if (!tree) return;
    tree.xp += amount;
    this.events.emit('tree-xp-gained', { treeName, skillId: treeName, xpGained: amount, totalXp: tree.xp, uses: tree.uses });
    this.checkUnlocks(treeName);
  }

  /**
   * Check for nodes that should unlock based on current XP and fork choices.
   * Emits fork-choice-available for forks, node-unlocked for auto-unlocks.
   */
  private checkUnlocks(treeName: string): void {
    if (this.suppressEvents) return;

    const tree = this.treeProgress.get(treeName);
    if (!tree) return;
    const treeDef = ConfigLoader.v4Trees[treeName];
    if (!treeDef) return;

    const allNodes = ConfigLoader.getV4AllNodes(treeName);

    for (const node of allNodes) {
      if (this.firedUnlocks.has(node.id)) continue;
      if (node.xpThreshold > tree.xp) continue;

      // Check reachability given current fork choices
      if (!ConfigLoader.isV4NodeReachable(treeName, node.id, tree.forkChoices)) continue;

      // Is this node a fork option?
      const forkInfo = ConfigLoader.getV4ForkForNode(node.id);
      if (forkInfo && forkInfo.treeName === treeName) {
        const fork = forkInfo.fork;
        // Only fire fork event once (when the first option becomes available)
        if (!tree.forkChoices[fork.forkId] && !this.firedUnlocks.has(`fork:${fork.forkId}`)) {
          // Check that fork parent is also reachable and unlocked
          const forkParent = allNodes.find(n => n.id === fork.forkId);
          if (forkParent && forkParent.xpThreshold <= tree.xp) {
            this.firedUnlocks.add(`fork:${fork.forkId}`);
            this.events.emit('fork-choice-available', {
              treeName,
              forkId: fork.forkId,
              options: fork.options,
            } as ForkEvent);
          }
        }
        continue; // Fork options aren't auto-unlocked — player must choose
      }

      // Apotheosis: check if any parent is unlocked
      const apoNode = treeDef.apotheosis.find(a => a.id === node.id);
      if (apoNode) {
        const anyParentUnlocked = apoNode.parentIds.some(pid => this.firedUnlocks.has(pid));
        if (!anyParentUnlocked) continue;
      }

      // Auto-unlock this node
      this.firedUnlocks.add(node.id);
      this.events.emit('node-unlocked', {
        treeName,
        nodeId: node.id,
        nodeName: node.name,
        nodeType: node.nodeType,
      } as NodeUnlockEvent);
    }
  }

  /** Record a fork choice — player picks one Active at a fork point. */
  chooseFork(treeName: string, forkId: string, chosenNodeId: string): boolean {
    const tree = this.treeProgress.get(treeName);
    if (!tree) return false;

    const treeDef = ConfigLoader.v4Trees[treeName];
    if (!treeDef) return false;

    // Validate the fork exists and the chosen node is an option
    const fork = treeDef.forks.find(f => f.forkId === forkId);
    if (!fork || !fork.options.includes(chosenNodeId)) return false;

    // Don't allow re-choosing a fork (must respec first)
    if (tree.forkChoices[forkId]) return false;

    tree.forkChoices[forkId] = chosenNodeId;

    // Mark the chosen node as unlocked
    this.firedUnlocks.add(chosenNodeId);

    this.events.emit('fork-chosen', { treeName, forkId, chosenNodeId });
    this.events.emit('node-unlocked', {
      treeName,
      nodeId: chosenNodeId,
      nodeName: ConfigLoader.getV4Node(chosenNodeId)?.name ?? chosenNodeId,
      nodeType: 'active',
    } as NodeUnlockEvent);

    // Re-check unlocks — choosing a fork may unlock downstream nodes
    this.checkUnlocks(treeName);

    return true;
  }

  /** Return true if the node is unlocked (auto-unlocked via XP threshold, or fork-chosen). */
  isUnlocked(skillId: string): boolean {
    const treeName = ConfigLoader.getV4SkillTree(skillId);
    if (!treeName) return false;
    const tree = this.treeProgress.get(treeName);
    if (!tree) return false;

    const node = ConfigLoader.getV4Node(skillId);
    if (!node) return false;

    // XP check
    if (node.xpThreshold > tree.xp) return false;

    // Reachability check (fork choices)
    if (!ConfigLoader.isV4NodeReachable(treeName, skillId, tree.forkChoices)) return false;

    // Fork option check: must have been chosen
    const forkInfo = ConfigLoader.getV4ForkForNode(skillId);
    if (forkInfo && forkInfo.treeName === treeName) {
      const fork = forkInfo.fork;
      return tree.forkChoices[fork.forkId] === skillId;
    }

    // Apotheosis: check any parent unlocked
    const treeDef = ConfigLoader.v4Trees[treeName];
    const apoNode = treeDef?.apotheosis.find(a => a.id === skillId);
    if (apoNode) {
      return apoNode.parentIds.some(pid => this.isUnlocked(pid));
    }

    return true;
  }

  /** Return all unlocked Active skill IDs across all trees (for hotbar selection). */
  getUnlockedActives(): string[] {
    const actives: string[] = [];
    const evolutions = new Set<string>(); // Track what gets replaced

    for (const treeName of Object.keys(ConfigLoader.v4Trees)) {
      const allNodes = ConfigLoader.getV4AllNodes(treeName);
      for (const node of allNodes) {
        if (!this.isUnlocked(node.id)) continue;
        if (node.nodeType === 'active') {
          actives.push(node.id);
        }
        if (node.nodeType === 'evolution' && 'replacesId' in node && node.replacesId) {
          evolutions.add(node.replacesId);
          actives.push(node.id);
        }
      }
    }

    // Remove replaced skills (evolution replaces parent)
    return actives.filter(id => !evolutions.has(id));
  }

  /** Return all unlocked Passive skill IDs across all trees (always-on). */
  getUnlockedPassives(): string[] {
    const passives: string[] = [];
    for (const treeName of Object.keys(ConfigLoader.v4Trees)) {
      const allNodes = ConfigLoader.getV4AllNodes(treeName);
      for (const node of allNodes) {
        if (node.nodeType === 'passive' && this.isUnlocked(node.id)) {
          passives.push(node.id);
        }
      }
    }
    return passives;
  }

  /** Get the effective skill ID for a given skill — follows evolution chain. */
  getEffectiveSkill(skillId: string): string {
    const treeName = ConfigLoader.getV4SkillTree(skillId);
    if (!treeName) return skillId;

    // Check if any evolution replaces this skill and is unlocked
    const allNodes = ConfigLoader.getV4AllNodes(treeName);
    for (const node of allNodes) {
      if (node.nodeType === 'evolution' && 'replacesId' in node && node.replacesId === skillId) {
        if (this.isUnlocked(node.id)) {
          return this.getEffectiveSkill(node.id); // Chain evolutions
        }
      }
    }
    return skillId;
  }

  /** Get all active upgrades (and passives with enhancesId) for a given skill ID. */
  getActiveUpgrades(skillId: string): V4NodeDefinition[] {
    const treeName = ConfigLoader.getV4SkillTree(skillId);
    if (!treeName) return [];

    const allNodes = ConfigLoader.getV4AllNodes(treeName);
    return allNodes.filter(n =>
      (n.nodeType === 'upgrade' || n.nodeType === 'passive') &&
      'enhancesId' in n &&
      (n as V4NodeDefinition).enhancesId === skillId &&
      this.isUnlocked(n.id),
    ) as V4NodeDefinition[];
  }

  /** Return the accumulated XP for the named tree. */
  getTreeXp(treeName: string): number {
    return this.treeProgress.get(treeName)?.xp ?? 0;
  }

  /** Return the full V4 progress record for the named tree. */
  getTreeProgress(treeName: string): V4TreeProgress {
    return this.treeProgress.get(treeName) ?? { xp: 0, uses: 0, forkChoices: {} };
  }

  /** Success chance for a skill based on mastery (tree XP earned past the node's unlock threshold). Passive/upgrade nodes always return 1.0. */
  getSkillSuccessChance(skillId: string): number {
    const treeName = ConfigLoader.getV4SkillTree(skillId);
    const treeXp = treeName ? this.getTreeXp(treeName) : 0;
    return computeSkillSuccessChance(skillId, treeXp);
  }

  /** Roll fizzle check for a skill. Returns true if the skill fires successfully. */
  rollSkillSuccess(skillId: string): boolean {
    const treeName = ConfigLoader.getV4SkillTree(skillId);
    const treeXp = treeName ? this.getTreeXp(treeName) : 0;
    return rollSkillSuccessFn(skillId, treeXp);
  }

  /** Return the sum of XP across all trees. */
  getTotalXP(): number {
    let total = 0;
    for (const tree of this.treeProgress.values()) {
      total += tree.xp;
    }
    return total;
  }

  /** Returns the highest tier reached by tree XP (approximate, for UI display). Reads thresholds from skill-progression.json. */
  getCurrentTier(treeName: string): 'foundation' | 'threshold' | 'transcendent' | 'apotheosis' {
    const xp = this.getTreeXp(treeName);
    const t = ConfigLoader.treeXpThresholds;
    if (t.apotheosis && xp >= t.apotheosis.base) return 'apotheosis';
    if (t.transcendent && xp >= t.transcendent.base) return 'transcendent';
    if (t.threshold && xp >= t.threshold.base) return 'threshold';
    return 'foundation';
  }

  /** Reset all fork choices for a tree (free respec). XP is kept. */
  respec(treeName: string): void {
    const tree = this.treeProgress.get(treeName);
    if (!tree) return;

    tree.forkChoices = {};

    // Re-derive unlocks without firing events
    this.suppressEvents = true;
    this.firedUnlocks = new Set();
    for (const tn of Object.keys(ConfigLoader.v4Trees)) {
      // Seed xpThreshold-0 nodes so they don't re-fire on next XP gain
      for (const node of ConfigLoader.getV4AllNodes(tn)) {
        if (node.xpThreshold === 0) this.firedUnlocks.add(node.id);
      }
      this.checkUnlocks(tn);
    }
    this.suppressEvents = false;

    this.events.emit('tree-respec', { treeName });
  }

  /** Export all progress for saving. */
  exportProgress(): Record<string, V4TreeProgress> {
    return exportTreeProgress(this.treeProgress);
  }

  /** Import saved V4 progress. */
  importProgress(saved: Record<string, unknown>): void {
    this.suppressEvents = true;
    this.firedUnlocks = importTreeProgress(saved, this.treeProgress);
    this.suppressEvents = false;
  }

  /** Look up the V4 node definition by ID. */
  getSkillDefinition(skillId: string): (V4NodeDefinition | V4ApotheosisDefinition) | undefined {
    return ConfigLoader.getV4Node(skillId);
  }

  /** Return pending forks that need choices (XP reached, no choice made yet). */
  getPendingForks(treeName: string): ForkEvent[] {
    const tree = this.treeProgress.get(treeName);
    if (!tree) return [];
    const treeDef = ConfigLoader.v4Trees[treeName];
    if (!treeDef) return [];

    const pending: ForkEvent[] = [];
    for (const fork of treeDef.forks) {
      if (tree.forkChoices[fork.forkId]) continue; // Already chosen

      // Check if the fork parent is reachable and has enough XP
      const forkParent = ConfigLoader.getV4AllNodes(treeName).find(n => n.id === fork.forkId);
      if (!forkParent || forkParent.xpThreshold > tree.xp) continue;
      if (!ConfigLoader.isV4NodeReachable(treeName, fork.forkId, tree.forkChoices)) continue;

      // Check if the fork options have enough XP
      const firstOption = ConfigLoader.getV4Node(fork.options[0]);
      if (!firstOption || firstOption.xpThreshold > tree.xp) continue;

      pending.push({ treeName, forkId: fork.forkId, options: fork.options });
    }
    return pending;
  }

  update(_delta: number): void {
    // No per-frame logic — progression is event-driven
  }

  destroy(): void {
    // Nothing to clean up
  }
}
